import {
    buildLaunchDarklyContext,
    launchDarklyClientSide,
} from '@insurer-frontend/client-feature-flags';
import {
    generateConfig,
    getApiBaseUrl,
    login,
    ROOT_CONFIG_LOCAL_STORAGE_KEY,
} from '@ptml/root-config';
import { registerApplication, start } from 'single-spa';
import { constructApplications, constructLayoutEngine, constructRoutes } from 'single-spa-layout';
import { initializeImportMap } from './import-map-helper';
import { header, surepart } from './loaders';
import microfrontendLayout from './microfrontend-layout.html?raw';
import { loadNewRelicAgent } from './newrelic/config';
import { fetchLaunchDarklyContextEnrichment } from './root-helper';
import { enableStaffEstimatingRollback, isIFESmokeTestAccount } from './staffestimating-rollback';
import {
    getSurePartBaseUrl,
    isLegacySurePartHostname,
    normaliseSurePartUrl,
} from './surepart-routing';
import { AuthenticationStatus } from './web-components/authentication-status/authentication-status';

// Register web components
import './web-components';

// Import design-tokens
import '@ptml/nexus-design-tokens/css';

const insurerRoles = new Set(['PTDeveloper', 'SystemAdmin', 'CompanyAdmin']);

if (!__TESTING__) {
    loadNewRelicAgent(null);
}

const initConfig = async (
    userData: NonNullable<Awaited<ReturnType<typeof login>>>,
    ldKey: string,
    useLaunchDarklyStreaming: boolean,
): Promise<void> => {
    const config = await generateConfig(userData, ldKey);

    const { user = null } = config;

    if (typeof globalThis !== 'undefined') {
        globalThis.localStorage.setItem(ROOT_CONFIG_LOCAL_STORAGE_KEY, JSON.stringify(config));
        globalThis.dispatchEvent(
            new StorageEvent('local-storage', {
                key: ROOT_CONFIG_LOCAL_STORAGE_KEY,
            }),
        );
    }

    const enrichment = await fetchLaunchDarklyContextEnrichment();

    const ldClient = await launchDarklyClientSide({
        envKey: config.launchDarklyClientId,
        context: buildLaunchDarklyContext(user, enrichment),
        streaming: useLaunchDarklyStreaming,
    });
    if (user?.principal_accountid && typeof globalThis.window.newrelic?.setUserId === 'function') {
        globalThis.window.newrelic.setUserId(user.principal_accountid);
    }

    if (useLaunchDarklyStreaming) {
        enableStaffEstimatingRollback(ldClient, isIFESmokeTestAccount(user));
    }
};

const initialize = (baseUrl: string): void => {
    const data = {
        loaders: {
            header,
            surepart,
        },
        props: {
            config: { baseUrl },
            handleError: (error: Error): void => {
                console.error('Error in root', error);
            },
        },
    };

    const { loadApp } = initializeImportMap();

    const routes = constructRoutes(
        microfrontendLayout.replace('__SUREPART_BASE_URL__', baseUrl),
        data,
    );
    const applications = constructApplications({
        routes,
        loadApp,
    });
    const layoutEngine = constructLayoutEngine({
        routes,
        applications,
    });

    for (const app of applications) {
        registerApplication(app);
    }
    layoutEngine.activate();
    start();
};

const useStaffEstimatingMigration = isLegacySurePartHostname();
const baseUrl = getSurePartBaseUrl();

normaliseSurePartUrl(baseUrl);

const bootstrap = async (): Promise<void> => {
    try {
        const userData = await login();

        if (!userData) {
            globalThis.location.href = `${getApiBaseUrl()}/fss/insurer/auth/login?returnUrl=${encodeURIComponent(globalThis.location.href)}`;
            return;
        }

        if (userData.roles.some((role) => insurerRoles.has(role))) {
            const ldKeyRaw = import.meta.env.VITE_LaunchDarkly_insurer_vnext_Client_Sdk_Key;
            await initConfig(
                userData,
                typeof ldKeyRaw === 'string' ? ldKeyRaw : '',
                useStaffEstimatingMigration,
            );
            initialize(baseUrl);
            AuthenticationStatus.removeFromDocument();
        } else {
            console.warn('User does not have the required roles to access the application');
            AuthenticationStatus.showRequiredRoleError();
        }
    } catch (error: unknown) {
        console.error('Error during login initialisation', error);
        AuthenticationStatus.showAuthenticationCheckError();
    }
};

// oxlint-disable-next-line unicorn/prefer-top-level-await
void bootstrap();
